import React from 'react';
import clsx from 'clsx';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../../constants/theme';

interface RoddoSectionCardProps {
  children: React.ReactNode;
  className?: string;
  tone?: 'soft' | 'solid' | 'reward';
  isDarkMode?: boolean;
}

const normalizeDescendants = [
  '[&_label]:text-[inherit]',
  '[&_p]:text-[inherit]',
  '[&_h1]:text-[inherit]',
  '[&_h2]:text-[inherit]',
  '[&_h3]:text-[inherit]',
  '[&_span]:text-[inherit]',
  '[&_button]:text-[inherit]',
  '[&_svg]:text-[inherit]',
  '[&_code]:text-[inherit]',
  '[&_small]:text-[inherit]',
  '[&_input]:text-[inherit]',
  '[&_textarea]:text-[inherit]',
  '[&_select]:text-[inherit]',
  '[&_input]:placeholder:text-black/40',
  '[&_textarea]:placeholder:text-black/40',
  'dark:[&_input]:placeholder:text-[#7F8BA0]',
  'dark:[&_textarea]:placeholder:text-[#7F8BA0]',
].join(' ');

const RoddoSectionCard: React.FC<RoddoSectionCardProps> = ({
  children,
  className,
  tone = 'soft',
  isDarkMode,
}) => {
  const dark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(dark);

  const toneStyle: React.CSSProperties =
    tone === 'solid'
      ? {
          background: dark ? '#101827' : '#FFF9EF',
          border: `1px solid ${dark ? 'rgba(132,151,190,0.16)' : 'rgba(74,58,42,0.14)'}`,
          color: dark ? '#F2F6FC' : '#30271E',
          boxShadow: dark ? '0 4px 12px rgba(0,0,0,0.13)' : '0 4px 14px rgba(89,65,37,0.06)',
        }
      : tone === 'reward'
      ? {
          background: dark ? '#171A20' : '#FFF3CC',
          border: `1px solid ${dark ? 'rgba(242,184,75,0.22)' : 'rgba(201,151,34,0.28)'}`,
          color: dark ? '#F8D88E' : '#4A3510',
          boxShadow: dark ? '0 4px 12px rgba(0,0,0,0.13)' : '0 4px 14px rgba(130,98,17,0.06)',
        }
      : {
          background: ui.cardBg,
          border: `1px solid ${ui.border}`,
          color: ui.textPrimary,
          boxShadow: dark ? '0 4px 12px rgba(0,0,0,0.12)' : '0 4px 12px rgba(89,65,37,0.05)',
        };

  return (
    <section
      key={dark ? 'dark' : 'light'}
      data-roddo-shell="section-card"
      data-roddo-tone={tone}
      className={clsx(
        'roddo-section-card roddo-consistency-card relative overflow-hidden rounded-[16px]',
        'px-4 py-4 sm:px-4.5 sm:py-4.5',
        normalizeDescendants,
        className,
      )}
      style={toneStyle}
    >
      <div className="relative z-10">{children}</div>
    </section>
  );
};

export default RoddoSectionCard;
